// Last Change (2025-05-29):
// - Removed direct localStorage access in favor of local-storage-service
// - Added sync-user-by-email-service for centralized API communication
// - AuthContext now only exposes `user` and `loading` state.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../firebase';
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
// No longer using direct Firestore access
import * as localStorageService from '../services/local-storage-service';
import * as syncUserByEmailService from '../services/sync-user-by-email-service';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          console.log('AuthContext: User authenticated with email:', currentUser.email);
          
          // Check if we need to force a refresh (set by useSubscription)
          const forceRefresh = localStorageService.isForceAuthRefreshSet();
          if (forceRefresh) {
            console.log('AuthContext: Forced refresh detected, clearing flag');
            localStorageService.clearForceAuthRefresh();
          }
          
          // Use the syncUserByEmailService to handle user data synchronization
          console.log('AuthContext: Calling syncUserByEmail service');
          
          const result = await syncUserByEmailService.syncUserByEmail(currentUser, true);
          
          if (result.status === 'success') {
            console.log('AuthContext: User document successfully updated');
          } else {
            console.error('AuthContext: Error from syncUserByEmail:', result.message);
            // No fallback to direct Firestore update - we rely exclusively on the cloud function
            setError('Failed to sync user data. Please try again later.');
          }
        } catch (e) {
          console.error('AuthContext: Error initializing user document:', e);
        }
      }
      setUser(currentUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const currentUser = result.user;
    
    try {
      console.log('loginWithGoogle: User authenticated with email:', currentUser.email);
      
      // Use the syncUserByEmailService to handle user data synchronization
      console.log('loginWithGoogle: Calling syncUserByEmail service');
      
      const result = await syncUserByEmailService.syncUserByEmail(currentUser, true);
      
      if (result.status === 'success') {
        console.log('loginWithGoogle: User document successfully updated');
      } else {
        console.error('loginWithGoogle: Error from syncUserByEmail:', result.message);
        // No fallback to direct Firestore update - we rely exclusively on the cloud function
        console.error('Failed to sync user data. Please try again later.');
      }
    } catch (e) {
      console.error('loginWithGoogle: error initializing user document:', e);
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
